import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Tab, NewsItem } from "../../../../interfaces/api/congresses";
import { getNews } from "../actions";
import { useSelector } from "react-redux";
import { RootState } from "../../../../redux/store";
import { FormBuilder } from "../../../../components/form/FormBuilder";
import { Inbox } from "lucide-react";

const MedicalUpdates = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const tabs = useSelector((state: RootState) => state.auth.tabs);
  const isLoadingTabs = useSelector(
    (state: RootState) => state.auth.isLoadingTabs,
  );
  const [tabData, setTabData] = useState<Tab | null>(null);
  const [isNewsLoading, setIsNewsLoading] = useState(true);
  const [news, setNews] = useState<NewsItem[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [formValues, setFormValues] = useState<Record<string, any>>({});
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const pageSize = 10;

  // Predefined color palette
  const colorPalette = [
    "#203476",
    "#1a1af5",
    "#5151f7",
    "#8c3abb",
    "#71b8cf",
    "#7fdbc1",
    "#4ecfac",
  ];

  // Function to get a random color from the palette
  const getRandomColor = () => {
    const randomIndex = Math.floor(Math.random() * colorPalette.length);
    return colorPalette[randomIndex];
  };

  useEffect(() => {
    const tabId = Number(id?.split("-").pop());
    const currentTab = tabs.find((tab: Tab) => tab.id === tabId);
    setTabData(currentTab || null);
    if (currentTab) {
      fetchNews({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, tabs]);

  const fetchNews = async (filters: Record<string, any>) => {
    try {
      setIsNewsLoading(true);
      const response = await getNews(
        Number(id?.split("-").pop()),
        pageSize,
        currentPage,
        filters,
      );
      if (response && response.content) {
        setNews(response.content.data);
        setTotalCount(response.content.total);
      } else {
        setNews([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error("Error fetching news:", error);
    } finally {
      setIsNewsLoading(false);
    }
  };

  const handleFormSubmit = async (values: Record<string, any>) => {
    // Store form values
    setFormValues(values);

    // Convert form values to filter parameters
    const filterParams: Record<string, number> = {};
    Object.entries(values).forEach(([key, value]) => {
      if (value) {
        filterParams[key] = Number(value);
      }
    });

    await fetchNews(filterParams);
  };

  const handlePageChange = async (newPage: number) => {
    setCurrentPage(newPage);
    // Convert form values to filter parameters
    const filterParams: Record<string, number> = {};
    Object.entries(formValues).forEach(([key, value]) => {
      if (value) {
        filterParams[key] = Number(value);
      }
    });
    // Scroll to top when changing pages
    window.scrollTo({ top: 0, behavior: "smooth" });
    await fetchNews(filterParams);
  };

  const handleNewsItemClick = (newsId: number) => {
    navigate(`/${id}/details/${newsId}`);
  };

  // Initial loading skeleton
  if (isLoadingTabs) {
    return (
      <div className="container mx-auto py-4">
        <div className="animate-pulse">
          <div className="flex flex-col lg:flex-row gap-6">
            {/* News Content Skeleton */}
            <div className="w-full lg:w-3/4">
              <div className="bg-white dark:bg-darkCardBackground rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                <div className="space-y-6">
                  {[...Array(5)].map((_, index) => (
                    <div
                      key={index}
                      className="flex gap-6 border-b border-gray-200 dark:border-gray-700 pb-6 last:border-0"
                    >
                      <div className="w-32 h-32 flex-shrink-0">
                        <div className="w-full h-full bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
                      </div>
                      <div className="flex-1">
                        <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2"></div>
                        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Filters Sidebar Skeleton */}
            <div className="hidden lg:block w-full lg:w-1/4">
              <div className="bg-white dark:bg-darkCardBackground rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-4"></div>
                <div className="space-y-4">
                  {[...Array(3)].map((_, index) => (
                    <div key={index} className="space-y-2">
                      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                      <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded"></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // News loading skeleton
  const NewsSkeleton = () => (
    <div className="space-y-6">
      {[...Array(5)].map((_, index) => (
        <div
          key={index}
          className="flex gap-6 border-b border-gray-200 dark:border-gray-700 pb-6 last:border-0"
        >
          <div className="w-32 h-32 flex-shrink-0">
            <div className="w-full h-full bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
          </div>
          <div className="flex-1">
            <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2 animate-pulse"></div>
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4 animate-pulse"></div>
          </div>
        </div>
      ))}
    </div>
  );

  // Empty state component
  const EmptyState = () => (
    <div className="flex flex-col items-center justify-center py-12">
      <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4">
        <Inbox className="w-8 h-8 text-gray-400 dark:text-gray-500" />
      </div>
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
        No News Found
      </h3>
      <p className="text-sm text-gray-500 dark:text-gray-400 text-center max-w-sm">
        Try adjusting your filters or check back later for new updates.
      </p>
    </div>
  );

  if (!tabData) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Tab Not Found
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            The requested tab could not be found.
          </p>
        </div>
      </div>
    );
  }

  // Create form schema from filters
  const formSchema = {
    type: "object",
    properties:
      tabData.filters?.reduce(
        (acc, filter) => ({
          ...acc,
          [filter.key]: {
            label: filter.label,
            component: "SelectField",
            required: false,
            componentProps: {
              options: filter.items.map((item) => ({
                label: item.value,
                value: item.id.toString(),
              })),
              placeholder: `Select ${filter.label}`,
            },
          },
        }),
        {},
      ) || {},
  };

  return (
    <div className="container mx-auto py-4">
      {/* Mobile Filters Accordion */}
      <div className="lg:hidden mb-6">
        <button
          onClick={() => setIsFiltersOpen(!isFiltersOpen)}
          className="w-full flex items-center justify-between p-4 bg-white dark:bg-darkCardBackground rounded-lg border border-gray-200 dark:border-gray-700"
        >
          <span className="font-semibold text-gray-900 dark:text-white">
            Filters
          </span>
          <svg
            className={`w-5 h-5 transform transition-transform ${isFiltersOpen ? "rotate-180" : ""}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>
        {isFiltersOpen && (
          <div className="mt-2 p-4 bg-white dark:bg-darkCardBackground rounded-lg border border-gray-200 dark:border-gray-700">
            <FormBuilder
              schema={formSchema}
              onSubmit={handleFormSubmit}
              buttonType="submit"
              gridConfig={{ mobile: 1, desktop: 1 }}
              submitButtonClassName="w-[100%]"
              initialValues={formValues}
              resetOnSubmit={false}
            />
          </div>
        )}
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* News Content */}
        <div className="w-full lg:w-3/4">
          <div className="bg-white dark:bg-darkCardBackground rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            {isNewsLoading ? (
              <NewsSkeleton />
            ) : (
              <>
                {news.length > 0 ? (
                  <>
                    <div className="space-y-6">
                      {news.map((item) => (
                        <div
                          key={item.id}
                          className="border-b border-gray-200 dark:border-gray-700 pb-6 last:border-0"
                        >
                          <div
                            className="flex gap-6 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors duration-200 rounded-lg p-4"
                            onClick={() => handleNewsItemClick(item.id)}
                          >
                            {/* Image or Subspeciality Card */}
                            <div className="w-32 h-32 flex-shrink-0">
                              {item.img ? (
                                <img
                                  src={item.img}
                                  alt={item.title}
                                  className="w-full h-full object-cover rounded-lg"
                                />
                              ) : (
                                <div
                                  className="w-full h-full rounded-lg flex items-center justify-center p-4 text-center"
                                  style={{
                                    backgroundColor: getRandomColor(),
                                    color: "#ffffff",
                                  }}
                                >
                                  <span className="font-medium text-sm">
                                    {item.subspeciality ||
                                      item.specialty ||
                                      "Medical News"}
                                  </span>
                                </div>
                              )}
                            </div>

                            {/* Content */}
                            <div className="flex-1">
                              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2 line-clamp-2 group-hover:text-primary-600 dark:group-hover:text-primary-400">
                                {item.title}
                              </h3>
                              <p className="text-sm text-gray-500 dark:text-gray-400">
                                {new Date(item.publishdate).toLocaleDateString(
                                  "en-US",
                                  {
                                    year: "numeric",
                                    month: "long",
                                    day: "numeric",
                                  },
                                )}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Pagination */}
                    {totalCount > pageSize && (
                      <div className="mt-6 flex justify-center">
                        <nav className="flex items-center gap-2">
                          <button
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700"
                          >
                            Previous
                          </button>
                          <span className="text-sm text-gray-600 dark:text-gray-400">
                            Page {currentPage} of{" "}
                            {Math.ceil(totalCount / pageSize)}
                          </span>
                          <button
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={
                              currentPage >= Math.ceil(totalCount / pageSize)
                            }
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700"
                          >
                            Next
                          </button>
                        </nav>
                      </div>
                    )}
                  </>
                ) : (
                  <EmptyState />
                )}
              </>
            )}
          </div>
        </div>

        {/* Desktop Filters Sidebar */}
        <div className="hidden lg:block w-full lg:w-1/4 relative">
          <div className="bg-white dark:bg-darkCardBackground rounded-lg border border-gray-200 dark:border-gray-700 p-4 sticky top-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Filters
            </h2>
            <FormBuilder
              schema={formSchema}
              onSubmit={handleFormSubmit}
              buttonType="submit"
              gridConfig={{ mobile: 1, desktop: 1 }}
              submitButtonClassName="w-[100%]"
              initialValues={formValues}
              resetOnSubmit={false}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MedicalUpdates;
